import { gql } from "@apollo/client";
import { Spinner } from "@chakra-ui/react";
import { useAuthContext } from "../../auth";
import { CategorySettingsForm } from "../../components/CategorySettingsForm";
import { toaster } from "../../components/ui/toaster";
import {
  type Categories_Insert_Input,
  type Categories_Set_Input,
  CategoriesDocument,
  type CategoriesQuery,
  useCategoriesQuery,
  useCreateCategoryMutation,
  useDeleteCategoriesMutation,
  useReorderCategoriesMutation,
  useUpdateCategoryMutation,
} from "../../graphql/generated/apollo";
import { useShopContext } from "../../shop";

gql`
  query Categories($shop_id: uuid!) {
    categories(where: { shop_id: { _eq: $shop_id } }, order_by: [{ position: asc }, { name: asc }]) {
      id
      name
      position
      category_templates {
        template {
          id
          name
        }
      }
    }
  }

  mutation ReorderCategories($categories: [categories_insert_input!]!) {
    insert_categories(objects: $categories, on_conflict: { constraint: categories_pkey, update_columns: [position] }) {
      affected_rows
      returning {
        id
        name
        position
      }
    }
  }

  mutation UpdateCategory($id: uuid!, $name: String!) {
    update_categories_by_pk(pk_columns: { id: $id }, _set: { name: $name }) {
      id
      name
      position
    }
  }

  mutation CreateCategory($category: categories_insert_input!) {
    insert_categories_one(object: $category) {
      id
      name
      position
    }
  }

  mutation DeleteCategories($ids: [uuid!]!) {
    delete_categories(where: { id: { _in: $ids } }) {
      affected_rows
      returning {
        id
      }
    }
  }
`;

export function Categories() {
  const { user, isLoading: authLoading } = useAuthContext();
  const { currentShopId, isLoading: shopLoading } = useShopContext();

  const { data, loading, error } = useCategoriesQuery({
    variables: {
      shop_id: currentShopId ?? "",
    },
    skip: !user || !currentShopId,
  });

  // 全カテゴリーの並び順更新に使用
  const [reorderCategories] = useReorderCategoriesMutation({
    refetchQueries: [
      {
        query: CategoriesDocument,
        variables: { shop_id: currentShopId ?? "" },
      },
    ],
    onCompleted: () => {
      toaster.create({
        title: "カテゴリーの順序を更新しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  // 単一カテゴリーの作成
  const [createCategory] = useCreateCategoryMutation({
    onCompleted: () => {
      toaster.create({
        title: "カテゴリーを追加しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  // 単一カテゴリーの更新
  const [updateCategory] = useUpdateCategoryMutation({
    onCompleted: () => {
      toaster.create({
        title: "カテゴリーを更新しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  // カテゴリーの削除
  const [deleteCategories] = useDeleteCategoriesMutation({
    onCompleted: () => {
      toaster.create({
        title: "カテゴリーを削除しました",
        type: "success",
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: "エラーが発生しました",
        description: error.message,
        type: "error",
      });
    },
  });

  if (authLoading || shopLoading || loading) {
    return <Spinner />;
  }

  if (error) {
    toaster.create({
      title: "エラーが発生しました",
      description: error.message,
      type: "error",
    });
  }

  const handleAddCategory = (category: Categories_Insert_Input) => {
    createCategory({
      variables: {
        category: {
          ...category,
          shop_id: currentShopId ?? "",
        },
      },
      update(cache, { data }) {
        if (!data?.insert_categories_one) {
          return;
        }

        const existingCategories = cache.readQuery<CategoriesQuery>({
          query: CategoriesDocument,
          variables: { shop_id: currentShopId ?? "" },
        }) ?? { categories: [] };

        cache.writeQuery({
          query: CategoriesDocument,
          variables: { shop_id: currentShopId ?? "" },
          data: {
            categories: [...existingCategories.categories, data.insert_categories_one],
          },
        });
      },
    });
  };

  const handleUpdateCategory = (category: Categories_Set_Input) => {
    if (!category.id) return;

    updateCategory({
      variables: {
        id: category.id,
        name: category.name ?? "",
      },
    });
  };

  const handleDeleteCategory = (id: string) => {
    deleteCategories({
      variables: {
        ids: [id],
      },
      update: (cache, { data }) => {
        if (!data?.delete_categories) {
          return;
        }

        cache.evict({
          id: cache.identify({ __typename: "categories", id }),
        });
      },
    });
  };

  const handleReorderCategories = (categories: Categories_Insert_Input[]) => {
    reorderCategories({
      variables: {
        categories: categories.map((category) => ({
          id: category.id,
          name: category.name,
          position: category.position,
          shop_id: currentShopId ?? "",
        })),
      },
    });
  };

  return (
    <CategorySettingsForm
      categories={data?.categories || []}
      onAdd={handleAddCategory}
      onUpdate={handleUpdateCategory}
      onDelete={handleDeleteCategory}
      onReorder={handleReorderCategories}
    />
  );
}
